import { useCallback, useEffect, useRef } from 'react';
import { cn } from '@waymail/ui';

export interface ResizerProps {
  value: number;
  min: number;
  max: number;
  onChange: (width: number) => void;
  label: string;
}

/**
 * Separador arrastavel entre paineis. `role="separator"` com valor e faixa,
 * navegavel por seta (Secao 6.10) - arrastar com mouse nao pode ser o unico jeito.
 */
export function Resizer({ value, min, max, onChange, label }: ResizerProps) {
  const arrastando = useRef(false);
  const inicio = useRef({ x: 0, width: 0 });

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      arrastando.current = true;
      inicio.current = { x: event.clientX, width: value };
      event.currentTarget.setPointerCapture(event.pointerId);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [value],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!arrastando.current) return;
      onChange(inicio.current.width + (event.clientX - inicio.current.x));
    },
    [onChange],
  );

  const encerrar = useCallback(() => {
    arrastando.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Se o ponteiro sumir (janela perde foco, aba troca), o cursor nao pode ficar preso.
  useEffect(() => encerrar, [encerrar]);

  function onKeyDown(event: React.KeyboardEvent) {
    const passo = event.shiftKey ? 40 : 8;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      onChange(value - passo);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      onChange(value + passo);
    } else if (event.key === 'Home') {
      event.preventDefault();
      onChange(min);
    } else if (event.key === 'End') {
      event.preventDefault();
      onChange(max);
    }
  }

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={value}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={encerrar}
      onPointerCancel={encerrar}
      onKeyDown={onKeyDown}
      className={cn(
        'group relative w-1 shrink-0 cursor-col-resize touch-none bg-subtle transition-colors',
        'hover:bg-wc-blue-40 focus-visible:bg-wc-blue',
      )}
    >
      {/* Area de acerto de 9px sem ocupar 9px de layout. */}
      <span aria-hidden="true" className="absolute inset-y-0 -left-1 w-[9px]" />
    </div>
  );
}
